/* eslint-disable consistent-return */
import { Alert, Button, Card, Col, Layout, Row, Spin, Typography } from "antd";

import { getSupaClient } from "../../lib/supabase";
import { createDiscordUrl } from "../../utils/constants";
import { User } from "@supabase/supabase-js";
import { useQuery, useMutation } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { DiscordButton } from "./DiscordButton";
import { setOnboardingStatus } from "../Onboarding/Onboarding";
import DotsFishLoading from "../DotsFishLoading/DotsFishLoading";
import { FileSearchOutlined } from "@ant-design/icons";
const { Title, Text } = Typography;

const { Content } = Layout;

const NoSession = () => (
  <div>
    <Content className="text-center">
      <Row style={{ marginTop: "100px" }}>
        <Col span={8} offset={8}>
          <Alert
            message="No session"
            description="We couldn't find a user session. Please contact us for support at hello@dots.community!"
            type="error"
          />
        </Col>
      </Row>
      <Row style={{ marginTop: "10px" }}>
        <Col span={8} offset={8}>
          <a href="/signin">
            <Button type="link">Back to Sign In </Button>
          </a>
        </Col>
      </Row>
    </Content>
  </div>
);

type GuildRecord = {
  id: string;
  name: string;
  icon: string;
  has_dots_bot: boolean;
  has_required_permissions: boolean;
  can_user_add_bot: boolean;
  // Flag to check if the have the user in discord_users table
  is_user_a_dots_guild_member: boolean;
};

/**
 * 2908TODO documentation
 */
async function fetchGuilds(code: string) {
  const path = "/guilds";
  const fullURL = `${process.env["REACT_APP_API_URL"]}${path}?`;
  const authToken = getSupaClient().auth.session()?.access_token;
  const redirectUri = encodeURIComponent(
    `${process.env["REACT_APP_URI"]}/onboarding/discord/`
  );
  const response = await fetch(
    fullURL +
      new URLSearchParams({
        code,
        redirectUri,
      }),
    {
      method: "GET",
      headers: { Authorization: `Bearer ${authToken}` },
    }
  );
  if (response.ok) {
    const responseJson = await response.json();
    console.log("responseJson fetchGuilds", responseJson);
    return responseJson.guilds as GuildRecord[];
  } else {
    let reason = await response.text();
    throw new Error(reason);
  }
}

/**
 * Get a list of guilds that the user is in.
 *
 * Note: there are two cases:
 * 1. The first time this screen is shown. We send `code` from the URL parameter to backend, which obtains the list of guilds and stores the discord access token along with the user
 * 2. The re-entrance of this screen. At that time `code` isn't available. We send user id or auth token to the backend. Since we've stored the discord access token on the backend, we'll be able to get the list of guilds.
 *
 * See this ticket for more details:
 * https://app.shortcut.com/sedimentiq/story/2809/onboarding-flow-discord
 *
 * We use React Query to cache the list of guilds according to the currently authenticated user. We use user id as the cache key. That's why we pass it in as a parameter of the hook.
 *
 *
 * @param user
 * @param enabled
 * @returns A React Query result object that includes `data`, `isLoading`, `isError` etc. When loaded, `data` is an array of GuildRecord.
 */
function useGuilds(user: User | null, enabled: boolean = true) {
  const location = useLocation();
  return useQuery<GuildRecord[], Error>(
    ["guilds", { userId: user?.id }],
    async () => {
      const params = new URLSearchParams(location.search);
      const code = params.get("code");
      return await fetchGuilds(code ?? "");
    },
    { enabled }
  );
}

/**
 * 2908TODO documentation
 * @param guildId
 * @returns
 */
async function joinGuild(guildId: string) {
  const supabase = getSupaClient();
  const path = `/guilds/${guildId}/members`;
  const fullURL = `${process.env["REACT_APP_API_URL"]}${path}?`;
  const authToken = supabase.auth.session()?.access_token;
  const response = await fetch(fullURL, {
    method: "POST",
    headers: { Authorization: `Bearer ${authToken}` },
  });
  if (response.ok) {
    return response;
  } else {
    let reason = await response.text();
    throw new Error(reason);
  }
}

function useJoinGuildMutation() {
  return useMutation<any, Error, string>(async (guildId: string) => {
    return await joinGuild(guildId);
  });
}

const GuildCard = ({ g }: { g: GuildRecord }) => {
  const navigate = useNavigate();
  const joinGuildMutation = useJoinGuildMutation();

  const JoinButton = (
    <>
      <DiscordButton
        onClick={() => {
          if (!joinGuildMutation.error) {
            joinGuildMutation.mutate(g.id, {
              onSuccess: () => {
                const user = getSupaClient().auth.user();
                if (user?.email) {
                  window.analytics.track(
                    "Sign up - join team Discord onboarded"
                  );
                  setOnboardingStatus(user.email, "onboarded");
                  navigate("/home");
                }
              },
            });
          }
        }}
        disabled={joinGuildMutation.isLoading}
      >
        {joinGuildMutation.error && (
          <Alert
            showIcon
            message={`Failed to add guild ${g.name}`}
            description={
              joinGuildMutation.error.message.includes(
                "Currently we don't support switching accounts."
              ) ? (
                <>
                  {joinGuildMutation.error.message}
                  <div
                    className="hover:underline"
                    onClick={() => {
                      navigate("/signin");
                    }}
                  >
                    Click here to sign in{" "}
                  </div>
                </>
              ) : (
                joinGuildMutation.error.message
              )
            }
            style={{ width: "100%" }}
          />
        )}
        {joinGuildMutation.isLoading && (
          <span>
            <Spin />
            Joining
          </span>
        )}
        {!joinGuildMutation.isLoading && !joinGuildMutation.error && (
          <span>Join team</span>
        )}
      </DiscordButton>
    </>
  );
  {
    /* 

        There are 3 cases here:
        1 - The user is a member of the guild and the guild exist but doesn't have a Dots user (we show a button to join the guild).
        2 - The user is already a member of the guild and the guild has the bot (we don't show any button here).
        3 - The user is a member of the guild but the guild doesn't have the bot (we show a button to add the bot).
        */
  }
  const conditions = [
    {
      condition: !g.is_user_a_dots_guild_member && g.has_dots_bot,
      component: JoinButton,
    },
    {
      condition: g.has_dots_bot,
      component: <></>,
    },
    {
      condition: !g.has_dots_bot,
      component: (
        <a href={createDiscordUrl("add-bot", g.id)}>
          <DiscordButton>Add Dots bot</DiscordButton>
        </a>
      ),
    },
  ];

  return (
    <Card
      bodyStyle={{
        height: "100%",
      }}
    >
      <div className="flex flex-col justify-between items-center gap-4 h-full text-center">
        <img
          src={`https://cdn.discordapp.com/icons/${g.id}/${g.icon}.png?size=256`}
          width={75}
          height={75}
          onError={(e) =>
            ((e.target as any).src = "https://i.imgur.com/J95o5Tq.png")
          }
          alt="server icon"
        />
        <div>{g.name}</div>

        {conditions.find((c) => c.condition)?.component}
      </div>
    </Card>
  );
};

/**
 * Screen where the user can select a guild to join after he authorizes the app to access his Discord account.
 * @returns
 */
const GuildGrids = () => {
  const user = getSupaClient().auth.user();
  const [userGuilds, setUserGuilds] = useState<GuildRecord[] | null>(null);
  const {
    data: useGuildsResponse,
    isLoading,
    isError,
    error,
  } = useGuilds(user, userGuilds === null);
  const guilds = useGuildsResponse
    ? useGuildsResponse.filter((g) => g.can_user_add_bot)
    : [];
  const navigate = useNavigate();

  /**
   * In order to avoid useGuilds be called every time this component is rendered,
   * we just enable useGuilds hook when userGuilds is null, meaning userGuilds is null the first time this component is rendered.
   * in subsequent renders userGuilds will not be null and useGuilds will not be called unless
   */
  useEffect(() => {
    if (!userGuilds) {
      setUserGuilds(guilds);
    } else {
      const incomingGuildIds = guilds.map((g) => g.id);
      const newGuilds = userGuilds.filter(
        (g) => !incomingGuildIds.includes(g.id)
      );
      if (newGuilds.length > 0) {
        setUserGuilds(guilds);
      }
    }
  }, [useGuildsResponse]);

  useEffect(() => {
    if (!isLoading && !isError) {
      const email = user?.email;
      if (email) {
        setOnboardingStatus(email, "discord-pick-guilds");
        window.analytics.track("Sign up - discord pick guilds");
      }
    }
  }, [isError, isLoading]);

  // This useEffect is usefull when the user creates a new server, comes back to the onboarding flow and refresh the page with the code included in the path. In this page an error will be thrown because that code was already used, what we want to do here is to redirect to the same path whitput that query string
  useEffect(() => {
    if (
      error?.message &&
      error.message.includes("maybe it has been used already")
    ) {
      navigate("/onboarding/discord/", { replace: true });
      // I have to reload this page because navigate doesn't do that automatically for some reason
      window.location.reload();
    }
  }, [error]);

  return (
    <Content style={{ minHeight: "100vh" }} className="discord-all-set-bg">
      <Row justify="center" align="middle" style={{ minHeight: "100vh" }}>
        <Col span={16}>
          <div
            className="my-12 rounded-lg p-10 discord-all-set-card flex justify-center items-center"
            style={{ minHeight: "90vh" }}
          >
            {isLoading && (
              <div className="flex items-center justify-center flex-col">
                <DotsFishLoading />
                <p>Fetching your servers...</p>
              </div>
            )}
            {!isLoading && isError && (
              <Alert
                message="Error getting guilds"
                description={<>{error?.message}</>}
                type="error"
              />
            )}
            {!isLoading && !isError && !guilds.length && (
              <div className="flex flex-col justify-center items-center">
                <p className="text-4xl">
                  <FileSearchOutlined style={{ color: "#FC845E" }} />
                </p>
                <p className="text-lg w-7/12 text-center">
                  Sorry, you don't have the "Manage Server" permission in any
                  servers. Please head to the server you want to add Dots to and
                  request the "Manage Server" permission. In case you don't have
                  any server please create one.
                </p>
                <Button
                  type="primary"
                  style={{ fontSize: "1rem" }}
                  className="mt-4"
                  href="/onboarding/discord/"
                >
                  Then, come back and click here. Thanks!
                </Button>
              </div>
            )}
            {!!guilds.length && (
              <div className="flex flex-col justify-center items-center max-w-3xl m-auto gap-8">
                <Title>Your servers</Title>
                <Text>Already on Dots</Text>
                <div
                  style={{ gridTemplateColumns: "auto auto auto auto" }}
                  className="grid gap-x-3 gap-y-3"
                >
                  {guilds
                    .filter((g) => g.has_dots_bot)
                    .map((g) => (
                      <GuildCard g={g} key={g.id} />
                    ))}
                </div>
                <Text>Add Dots to a new server</Text>
                <div
                  style={{ gridTemplateColumns: "auto auto auto auto" }}
                  className="grid gap-x-3 gap-y-3"
                >
                  {guilds
                    .filter((g) => !g.has_dots_bot)
                    .map((g) => (
                      <GuildCard g={g} key={g.id} />
                    ))}
                </div>
              </div>
            )}
          </div>
        </Col>
      </Row>
    </Content>
  );
};

/**
 *
 * Component to render the path /onboarding/discord. There could be three different cases:
 * 1. The page was a redirect from Discord auth flow, and the user authorizes it. In such case, there is a `code` parameter in the URL. We want to display a list of guilds and set onboarding status to `discord-pick-guilds`.
 * 2. Redirect from Discord, but the user rejects it. In such case, there is an `error` parameter in the URL. We want to set onboarding status to `choose-platform` and redirect to `/onboarding`
 * 3. Redirect from our own code after the user signs in. In such case, no URL parameters will be present. We'll use userId or auth token to get the list of guilds (see useGuilds)
 *
 */
const DiscordPickGuild = () => {
  const supabase = getSupaClient();
  const session = supabase.auth.session();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("error")) {
      navigate("/onboarding");
    }
  }, [location.search]);

  if (session) {
    return <GuildGrids />;
  } else {
    return <NoSession />;
  }
};

export default DiscordPickGuild;
